import { useId } from 'react'
import styles from './Input.module.css'

export type InputType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search'

interface InputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: InputType
  label?: string
}

export function InputField({ label, className = '', id, ...rest }: InputFieldProps) {
  const cls = `${styles.input} ${className}`.trim()
  // Associate the label with the control so screen readers can tell they're
  // linked. Respect an explicit id from the caller; otherwise generate a
  // stable one (useId) so getByLabelText and real AT both work.
  const generatedId = useId()
  const inputId = id ?? (label ? generatedId : undefined)

  if (label) {
    return (
      <div className={styles.field}>
        <label htmlFor={inputId}>{label}</label>
        <input id={inputId} className={cls} {...rest} />
      </div>
    )
  }

  return <input className={cls} {...rest} />
}

interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'type'> {
  label?: string
}

export function TextArea({ label, className = '', id, ...rest }: TextAreaProps) {
  const cls = `${styles.input} ${className}`.trim()
  const generatedId = useId()
  const textAreaId = id ?? (label ? generatedId : undefined)

  if (label) {
    return (
      <div className={styles.field}>
        <label htmlFor={textAreaId}>{label}</label>
        <textarea id={textAreaId} className={cls} {...rest} />
      </div>
    )
  }

  return <textarea className={cls} {...rest} />
}

interface RadioGroupProps {
  name: string
  options: { value: string; label: string }[]
  value: string
  onChange: (value: string) => void
}

export function RadioGroup({ name, options, value, onChange }: RadioGroupProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {options.map((opt) => (
        <label key={opt.value} className={styles.radio}>
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          <span className={styles.dot} />
          {opt.label}
        </label>
      ))}
    </div>
  )
}
